import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Card, Checkbox, List } from "semantic-ui-react";
import TextInput from "../controlled/TextInput";
import { useTextResources } from "../../../language";
import './styles.css';

const ItemsList = ({
    items,
    onItemChecked,
    itemRender,
    hasSelectAll,
    onAllItemsChecked,
    hasSearch,
    searchSelector,
    disabled,
}) => {
    const text = useTextResources();
    const [searchValue, setSearchValue] = useState('');

    const handleSerachChange = useCallback((value) => {
        setSearchValue(value);
    }, []);

    const searchedItems = useMemo(() => items.filter(
        (item) => searchSelector(item.value, searchValue)),
        [items, searchValue, searchSelector],
    );

    const checkedItemsCount = useMemo(() => items.filter((item) => item.checked).length, [items]);
    const someItemsChecked = checkedItemsCount > 0;
    const areAllItemsChecked = someItemsChecked && checkedItemsCount === items.length;

    const handleAllItemsChecked = useCallback(() => {
        const itemsKeys = items.map((item) => item.key);
        onAllItemsChecked(itemsKeys, !areAllItemsChecked);
    }, [areAllItemsChecked, items, onAllItemsChecked]);

    const checkAllLabel = useMemo(() => {
        return checkedItemsCount > 0
            ? `${checkedItemsCount} / ${items.length} ${text("Items")}`
            : `${items.length} ${text("Items")}`;
    }, [items.length, checkedItemsCount, text]);

    const checkedSearchedItemsCount = useMemo(() => searchedItems.filter((item) => item.checked).length, [searchedItems]);
    const someSearchedItemsChecked = checkedSearchedItemsCount > 0;
    const areAllSearchedItemsChecked = someSearchedItemsChecked && checkedSearchedItemsCount === searchedItems.length;

    const handleAllSearchedItemsChecked = useCallback(() => {
        const searchedItemsKeys = searchedItems.map((item) => item.key);
        onAllItemsChecked(searchedItemsKeys, !areAllSearchedItemsChecked);
    }, [searchedItems, onAllItemsChecked, areAllSearchedItemsChecked]);

    const checkAllSearchedLabel = useMemo(() => {
        return checkedSearchedItemsCount > 0
            ? `${checkedSearchedItemsCount} / ${searchedItems.length} ${text("Items")}`
            : `${searchedItems.length} ${text("Items")}`;
    }, [searchedItems.length, checkedSearchedItemsCount, text]);

    return (
        <Card fluid>
            <Card.Content>
                {hasSelectAll && (
                <Card.Header>
                    <Checkbox
                        checked={areAllItemsChecked}
                        indeterminate={!areAllItemsChecked && someItemsChecked}
                        onChange={handleAllItemsChecked}
                        label={checkAllLabel}
                        disabled={disabled}
                    />
                    {hasSearch && (
                        <TextInput
                            icon="search"
                            iconPosition="left"
                            placeholder={text("Search")}
                            size="mini"
                            action={(
                                <Checkbox
                                    className="trsnsferItemsSearchCheckbox"
                                    checked={areAllSearchedItemsChecked}
                                    indeterminate={!areAllSearchedItemsChecked && someSearchedItemsChecked}
                                    onChange={handleAllSearchedItemsChecked}
                                    label={checkAllSearchedLabel}
                                    disabled={disabled}
                                />
                            )}
                            value={searchValue}
                            onChange={handleSerachChange}
                            disabled={disabled}
                        />
                    )}
                </Card.Header>
                )}
            </Card.Content>
            <Card.Content>
                <div className="transferItemsList">
                    <List selection size="medium">
                        {searchedItems.map((item) => 
                        <Item key={item.key} item={item} onChecked={onItemChecked} itemRender={itemRender} disabled={disabled} />
                        )}
                    </List>
                </div>
            </Card.Content>
        </Card>
    )
};

ItemsList.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        value: PropTypes.shape({}).isRequired,
        checked: PropTypes.bool.isRequired,
    })).isRequired,
    onItemChecked: PropTypes.func.isRequired,
    itemRender: PropTypes.func,
    hasSelectAll: PropTypes.bool,
    onAllItemsChecked: PropTypes.func.isRequired,
    hasSearch: PropTypes.bool,
    searchSelector: PropTypes.func,
    onSearchChange: PropTypes.func,
    disabled: PropTypes.bool,
}

const Item = ({
    item : {
        key,
        value,
        checked
    },
    onChecked,
    itemRender,
    disabled
}) => {
    const handleChecked = useCallback(() => {
        onChecked(key);
    }, [key, onChecked]);

    return (
        <List.Item>
            <Checkbox
                key={key}
                className="transferItem"
                checked={checked}
                onChange={handleChecked}
                label={itemRender(value)}
                disabled={disabled}
            />
        </List.Item>
    );
};

Item.propTypes = {
    item: PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        value: PropTypes.shape({}).isRequired,
        checked: PropTypes.bool.isRequired,
    }).isRequired,
    onChecked: PropTypes.func.isRequired,
    itemRender: PropTypes.func,
    disabled: PropTypes.bool,
}

export default ItemsList;